import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  FlowNavigation,
  useFlowNavigationFocus,
  usePrevious,
} from '@leagueplatform/genesis-core';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
  SubtreeNodeVisibilityWrapper,
} from '@leagueplatform/masonry-engine';
import { captureError } from '@leagueplatform/observability';

import type { MasonryFlowStep } from '../masonry-flow-step-renderer/masonry-flow-step-renderer.component';

import { useFlowNavigationFn } from '../../../utils/flow-step-navigation.util';
import { useNavigationProps } from '../../../utils/flow-navigation-helpers.util';
import { NavigatorProperties } from '../../../types/flow-navigation';
import { formatClassNames } from '../../../utils/format-masonry-class-name.util';
import { StepTransitionWrapper } from '../step-transition-wrapper/step-transition-wrapper.component';
import { STEP_TRANSITION_TIMEOUT } from '../step-transition-wrapper/constants';

export type MasonryFlowNodeProperties = {
  currentStepIndex?: number;
  isForm?: boolean;
  navigator?: NavigatorProperties;
};

export type MasonryFlowNodeActions = {
  onComplete: MasonryEngineNodeAction;
  onTrailingButtonClick?: MasonryEngineNodeAction;
  onLeadingButtonClick?: MasonryEngineNodeAction;
};

export type MasonryFlowNodeSections = {
  steps: MasonryFlowStep[];
};

export type MasonryFlowNode = MasonryEngineNode<
  'linearFlow',
  MasonryFlowNodeProperties,
  MasonryFlowNodeActions,
  MasonryFlowNodeSections
>;

export type MasonryFlowNodeRendererProps =
  MasonryEngineNodeRendererProps<MasonryFlowNode>;

export const MasonryFlowNodeRenderer = ({
  currentStepIndex,
  isForm,
  navigator,
  nodeData,
  steps,
  onComplete,
  onTrailingButtonClick,
  onLeadingButtonClick,
  rootElementRef,
  className,
  engineClassName,
  locatorId,
  css,
}: MasonryFlowNodeRendererProps) => {
  const [transitioningDirection, setTransitioningDirection] = useState<
    1 | -1 | 0
  >(0);
  const stepNodes = nodeData.sections.steps;
  const numSteps = stepNodes.length;
  const prevTransitioningDirection = usePrevious(transitioningDirection);

  // Use fallback for currentStepIndex when undefined or out of bounds
  const isCurrentStepIndexOutOfBounds =
    currentStepIndex == null || currentStepIndex >= numSteps;

  const resolvedCurrentStepIndex = isCurrentStepIndexOutOfBounds
    ? 0
    : currentStepIndex;

  // Track previous step index for focus management
  const prevCurrentStepIndex = usePrevious(resolvedCurrentStepIndex);

  // Log error if currentStepIndex is out of bounds
  useEffect(() => {
    if (isCurrentStepIndexOutOfBounds) {
      captureError(
        new Error(
          `MasonryFlowNodeRenderer - currentStepIndex is out of bounds: ${currentStepIndex}`,
        ),
      );
    }
  }, [currentStepIndex, isCurrentStepIndexOutOfBounds]);

  const currentStepIndexRef = useRef(resolvedCurrentStepIndex);
  currentStepIndexRef.current = resolvedCurrentStepIndex;

  // Use the flow step navigation utility
  const flowNavigationFn = useFlowNavigationFn({
    currentStepIndexRef,
    numSteps,
    flowNodeId: nodeData.id,
    onTrailingButtonClick,
    onLeadingButtonClick,
    onComplete,
    setTransitioningDirection,
  });

  const completionPercentage =
    numSteps > 0 && resolvedCurrentStepIndex >= 0
      ? Math.round(((resolvedCurrentStepIndex + 1) / numSteps) * 100)
      : 0;

  // Build navigation props using custom hook
  const navigationProps = useNavigationProps({
    navigator,
    flowNavigationFn,
    numSteps,
    currentStepIndex: resolvedCurrentStepIndex,
    completionPercentage,
    transitioningDirection,
  });

  const { leadingButtonRef, trailingButtonRef, navRef } =
    useFlowNavigationFocus({
      hasLeadingButton: !!navigationProps.leadingButton,
      hasTrailingButton: !!navigationProps.trailingButton,
      currentStepIndex: resolvedCurrentStepIndex,
      prevCurrentStepIndex,
      transitioningDirection,
      prevTransitioningDirection,
    });

  const enhancedNavigationProps = useMemo(() => {
    const enhanced = { ...navigationProps };

    if (enhanced.leadingButton) {
      enhanced.leadingButton = {
        ...enhanced.leadingButton,
        ref: leadingButtonRef as any, // Casting as any to satisfy the polymorphic ref type
      };
    }

    if (enhanced.trailingButton) {
      enhanced.trailingButton = {
        ...enhanced.trailingButton,
        ref: trailingButtonRef as any, // Casting as any to satisfy the polymorphic ref type
      };
    }

    return enhanced;
  }, [navigationProps, leadingButtonRef, trailingButtonRef]);

  // navBarPosition with fallback
  const navBarPosition = navigator?.position || 'top';

  // Render all steps with visibility control
  const allStepsElements = useMemo(() => {
    const stepElements = steps.map((step, index) => {
      const isCurrentStep = index === resolvedCurrentStepIndex;

      return (
        <StepTransitionWrapper
          key={stepNodes[index].id}
          nextIndex={resolvedCurrentStepIndex}
          timeout={STEP_TRANSITION_TIMEOUT}
          in={isCurrentStep}
          unmountOnExit={false}
          mountOnEnter={false}
        >
          <SubtreeNodeVisibilityWrapper isHidden={!isCurrentStep}>
            {step}
          </SubtreeNodeVisibilityWrapper>
        </StepTransitionWrapper>
      );
    });

    return isForm ? <form>{stepElements}</form> : stepElements;
  }, [isForm, resolvedCurrentStepIndex, stepNodes, steps]);

  return (
    <Box
      ref={rootElementRef}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
      css={css}
    >
      {navBarPosition === 'top' && (
        <FlowNavigation
          ref={navRef}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...enhancedNavigationProps}
        />
      )}
      {allStepsElements}
      {navBarPosition === 'bottom' && (
        <FlowNavigation
          ref={navRef}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...enhancedNavigationProps}
        />
      )}
    </Box>
  );
};
